/*
Fork addition (Stage G): one-click "Tidy layout" — rearranges every
card on the board into a readable left-to-right flow layout.

Positions only: recipes, counts, objectives, orientations and links
are never touched. The layout is a pure function of the link graph
plus the items' current positions (used as the initial within-column
order) and measured card heights (used for vertical stacking):

- depth = longest path from any source (an item with no incoming
  links), computed by DFS over the link graph; edges that would close
  a cycle are ignored while they are on the DFS stack (back-edges),
  so cyclic boards still get a finite layout;
- column x = depth * TIDY_COLUMN_PITCH — producers left, consumers
  right. Cards are a fixed 300px wide; the 375px pitch leaves a 75px
  gap and is an exact multiple of the 25px snap grid;
- within a column, items are ordered by barycenter passes (average
  row of linked neighbours, forward then backward, two rounds) to
  reduce link crossings, then stacked from y=0, each card offset by
  its own measured height + TIDY_ROW_GAP;
- items with no links at all go into their own column to the RIGHT
  of the main graph (depth maxDepth + 1), so stray cards never
  interleave with the flow;
- every resulting coordinate is snapped with the same grid helper
  the drag code uses (scripts/grid.ts).
*/
import {snapValueToGrid} from './grid';
import type {BlueprintModel} from './model/store';

export const TIDY_CARD_WIDTH = 300;
export const TIDY_COLUMN_GAP = 75;
export const TIDY_COLUMN_PITCH = TIDY_CARD_WIDTH + TIDY_COLUMN_GAP;
export const TIDY_ROW_GAP = 40;
//card height before the DOM measurement lands (freshly added item)
export const TIDY_FALLBACK_CARD_HEIGHT = 200;

export interface TidyLayoutNode {
    key: string;
    x: number;
    y: number;
    height: number;
}

export interface TidyLayoutEdge {
    //the producing item (its output feeds the consumer's input)
    producerKey: string;
    //the consuming item
    consumerKey: string;
}

export interface TidyLayoutPoint {
    x: number;
    y: number;
}

export function computeTidyLayout(
    nodes: readonly TidyLayoutNode[],
    edges: readonly TidyLayoutEdge[],
): Map<string, TidyLayoutPoint> {
    const result = new Map<string, TidyLayoutPoint>();
    if(!nodes.length)
        return result;

    const nodeByKey = new Map(nodes.map((node) => [node.key, node]));
    const producersOf = new Map<string, Set<string>>();
    const consumersOf = new Map<string, Set<string>>();
    const connected = new Set<string>();
    for(const {producerKey, consumerKey} of edges) {
        //a self-link carries no flow-order information
        if(producerKey === consumerKey)
            continue;
        if(!nodeByKey.has(producerKey) || !nodeByKey.has(consumerKey))
            continue;
        let producers = producersOf.get(consumerKey);
        if(!producers) {
            producers = new Set();
            producersOf.set(consumerKey, producers);
        }
        producers.add(producerKey);
        let consumers = consumersOf.get(producerKey);
        if(!consumers) {
            consumers = new Set();
            consumersOf.set(producerKey, consumers);
        }
        consumers.add(consumerKey);
        connected.add(producerKey);
        connected.add(consumerKey);
    }

    //depth = longest path from a source, DFS with back-edges ignored:
    //a producer still on the DFS stack reports -1 and does not count
    const depthMemo = new Map<string, number>();
    const visiting = new Set<string>();
    const depthOf = (key: string): number => {
        const memo = depthMemo.get(key);
        if(memo !== undefined)
            return memo;
        if(visiting.has(key))
            return -1;
        visiting.add(key);
        let depth = 0;
        for(const producerKey of producersOf.get(key) || []) {
            const producerDepth = depthOf(producerKey);
            if(producerDepth >= 0)
                depth = Math.max(depth, producerDepth + 1);
        }
        visiting.delete(key);
        depthMemo.set(key, depth);
        return depth;
    };

    const columns = new Map<number, string[]>();
    let maxDepth = 0;
    for(const key of connected) {
        const depth = depthOf(key);
        maxDepth = Math.max(maxDepth, depth);
        const column = columns.get(depth);
        if(column) {
            column.push(key);
        } else {
            columns.set(depth, [key]);
        }
    }
    //unlinked items: their own column, right of the main graph
    //(or column 0 when the board has no links at all)
    const isolatedKeys = nodes.filter((node) => !connected.has(node.key)).map((node) => node.key);
    if(isolatedKeys.length) {
        const isolatedDepth = connected.size ? (maxDepth + 1) : 0;
        const column = columns.get(isolatedDepth);
        if(column) {
            column.push(...isolatedKeys);
        } else {
            columns.set(isolatedDepth, [...isolatedKeys]);
        }
        maxDepth = Math.max(maxDepth, isolatedDepth);
    }

    //initial within-column order: the current on-board order
    const byCurrentPosition = (a: string, b: string) => {
        const nodeA = nodeByKey.get(a);
        const nodeB = nodeByKey.get(b);
        return ((nodeA?.y || 0) - (nodeB?.y || 0))
            || ((nodeA?.x || 0) - (nodeB?.x || 0))
            || a.localeCompare(b);
    };
    const rowOf = new Map<string, number>();
    for(const column of columns.values()) {
        column.sort(byCurrentPosition);
        column.forEach((key, index) => rowOf.set(key, index));
    }

    //barycenter passes over the flow columns: order each column by the
    //average row of its linked neighbours, forward (by producers) then
    //backward (by consumers); ties keep the previous relative order
    const barycenterSort = (column: string[], neighboursOf: Map<string, Set<string>>) => {
        const scoreOf = new Map<string, number>();
        for(const key of column) {
            let sum = 0;
            let count = 0;
            for(const neighbourKey of neighboursOf.get(key) || []) {
                const row = rowOf.get(neighbourKey);
                if(row !== undefined) {
                    sum += row;
                    count++;
                }
            }
            //no linked neighbours on that side: stay where we are
            scoreOf.set(key, count ? (sum / count) : (rowOf.get(key) || 0));
        }
        column.sort((a, b) => ((scoreOf.get(a) || 0) - (scoreOf.get(b) || 0))
            || ((rowOf.get(a) || 0) - (rowOf.get(b) || 0)));
        column.forEach((key, index) => rowOf.set(key, index));
    };
    for(let pass = 0; pass < 2; pass++) {
        for(let depth = 1; depth <= maxDepth; depth++) {
            const column = columns.get(depth);
            if(column)
                barycenterSort(column, producersOf);
        }
        for(let depth = maxDepth - 1; depth >= 0; depth--) {
            const column = columns.get(depth);
            if(column)
                barycenterSort(column, consumersOf);
        }
    }

    //assign positions: columns left to right, each stacked from y=0
    //by measured card height + row gap, snapped to the board grid.
    //snapping can shave at most half a grid cell (12.5px) off the gap,
    //so the stacked bands can never overlap.
    for(const [depth, column] of [...columns.entries()].sort((a, b) => a[0] - b[0])) {
        const x = snapValueToGrid(depth * TIDY_COLUMN_PITCH);
        let cursorY = 0;
        for(const key of column) {
            const y = snapValueToGrid(cursorY);
            result.set(key, {x, y});
            const height = nodeByKey.get(key)?.height || 0;
            cursorY = y + (height > 0 ? height : TIDY_FALLBACK_CARD_HEIGHT) + TIDY_ROW_GAP;
        }
    }
    return result;
}

//model-facing wrapper: pulls the graph straight off the blueprint
//(link.output is the producing IO, link.input the consuming IO)
export function computeTidyLayoutForModel(blueprint: BlueprintModel): Map<string, TidyLayoutPoint> {
    const nodes: TidyLayoutNode[] = [...blueprint.items].map((item) => ({
        key: item.key,
        x: item.rect.x,
        y: item.rect.y,
        height: item.rect.height,
    }));
    const edges: TidyLayoutEdge[] = [];
    for(const link of blueprint.links) {
        const producerKey = link.output?.ownerItem?.key;
        const consumerKey = link.input?.ownerItem?.key;
        if(producerKey && consumerKey)
            edges.push({producerKey, consumerKey});
    }
    return computeTidyLayout(nodes, edges);
}
